import React, { useState, useEffect, useMemo } from "react";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { DateRangePicker, DateRangeDelimiter } from "@material-ui/pickers";
import {
  withStyles,
  makeStyles,
  createStyles,
} from "@material-ui/core/styles";
import { Button, Typography, MenuItem, TextField} from "@material-ui/core";
import * as Var from "styles/variables";
import {
  getPictreePlotFilterEventOptions,
  getPictreePlotFilterMunicipalityOptions,
  setDateRangePicTree,
  setPlotMunicipality,
  setSelectedSeason,
} from "actions";
const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
      flexGrow: 1,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: -12,
    },
    container: {
      display: "flex",
      maxWidth: 600,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      alignItems: "baseline",
    },
    delimiter: {
      paddingTop: theme.spacing(1),
    },
    dataBtn: {
      marginTop: theme.spacing(1.2),
      marginLeft: theme.spacing(1),
      height: theme.spacing(4),
      color: Var.btnLabelColor,
    },
    grow: {
      flexGrow: 1,
    },
    sectionCalendar: {
      display: "flex",
      marginRight: theme.spacing(1),
    },
    picTreePredefined: {
      marginRight: theme.spacing(2),
    },
    dateField: {
      width: 120,
      marginLeft: theme.spacing(1),
    },
    timeField: {
      width: 120,
      marginLeft: theme.spacing(1),
    },
    filterGroup: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
      marginTop: 8,
    },
    filterLabel: {
      color: "#111827",
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      flexShrink: 0,
    },
  })
);

const normalizePrefectureNames = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizePrefectureIds = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const CalendarRangePickerPlot = ({
  page,
  dateRangePicTree,
  plotMunicipality,
  setDateRangePicTree,
  setPlotMunicipality,
  setSelectedSeason,
  getPictreePlotFilterEventOptions,
  getPictreePlotFilterMunicipalityOptions,
  maxRange = null,
  plotEventOptions,
  plotMunicipalityOptions,
  mDate,
  allowOutOfSeason = false,
  onGoClick,
  fetchFilterEventOptions = true,
  fetchFilterMunicipalityOptions = true,
}) => {
  const classes = useStyles();
  const todayJst = moment().tz("Asia/Tokyo").startOf("day");
  const defaultStartDate = allowOutOfSeason
    ? (mDate ? moment.tz(mDate, "YYYY/MM/DD", "Asia/Tokyo").startOf("day").format("YYYY-MM-DD") : todayJst.format("YYYY-MM-DD"))
    : todayJst.clone().month(6).date(20).format("YYYY-MM-DD");
  const defaultEndDate = todayJst.format("YYYY-MM-DD");
  const [event, setEvent] = useState("all");
  const [municipality, setMunicipality] = useState("全体");
  const [dateRangeValue, setDateRangeValue] = useState([null, null]);
  const [error, setError] = useState([null, null]);
  const [minDate] = useState(defaultStartDate);
  const [maxDate] = useState(defaultEndDate);

  const normalizedEventOptions = useMemo(() => (
    Array.isArray(plotEventOptions)
      ? plotEventOptions.map((option) => ({
        value: String(option?.value || ""),
        label: String(option?.label || option?.value || ""),
        prefectureAreaIds: normalizePrefectureIds(option?.prefecture_area_ids || option?.prefectureAreaIds),
        prefectureNames: normalizePrefectureNames(option?.prefecture_names || option?.prefectureNames),
      })).filter((option) => option.value)
      : []
  ), [plotEventOptions]);
  const normalizedMunicipalityOptions = useMemo(() => (
    Array.isArray(plotMunicipalityOptions) && plotMunicipalityOptions.length > 0
      ? plotMunicipalityOptions.map((option) => ({
        value: String(option?.value || ""),
        label: String(option?.label || option?.value || ""),
        prefectureAreaId: String(option?.prefecture_area_id || option?.prefectureAreaId || ""),
        prefectureName: String(option?.prefecture_name || option?.prefectureName || ""),
      })).filter((option) => option.value)
      : [{ value: "全体", label: "全体", prefectureAreaId: "", prefectureName: "" }]
  ), [plotMunicipalityOptions]);
  const selectedEventOption = normalizedEventOptions.find((option) => String(option.value) === String(event));
  const activePrefectureAreaIds = event === "all" ? [] : (selectedEventOption?.prefectureAreaIds || []);
  const displayMunicipalityOptions = activePrefectureAreaIds.length > 0
    ? normalizedMunicipalityOptions.filter((option) =>
      !option.prefectureAreaId || activePrefectureAreaIds.includes(option.prefectureAreaId)
    )
    : normalizedMunicipalityOptions;

  useEffect(() => {
    const defaultRange = [defaultStartDate, defaultEndDate];
    if (
      !Array.isArray(dateRangePicTree) ||
      !dateRangePicTree[0] ||
      !dateRangePicTree[1] ||
      dateRangePicTree[0] !== defaultStartDate ||
      dateRangePicTree[1] !== defaultEndDate
    ) {
      setDateRangePicTree(defaultRange);
      setDateRangeValue(defaultRange);
      return;
    }

    setDateRangeValue(dateRangePicTree);
  }, [dateRangePicTree, defaultStartDate, defaultEndDate, setDateRangePicTree]);

  useEffect(() => {
    setPlotMunicipality(municipality);
  }, [municipality, setPlotMunicipality]);

  useEffect(() => {
    if (event === "all") {
      setSelectedSeason([]);
      return;
    }

    const matchedEvent = normalizedEventOptions.find((option) => String(option.value) === String(event));

    setSelectedSeason(
      matchedEvent
        ? [{
          seasonId: String(matchedEvent.value),
          seasonName: matchedEvent.label,
          prefectureAreaIds: matchedEvent.prefectureAreaIds,
          prefectureNames: matchedEvent.prefectureNames,
        }]
        : []
    );
  }, [event, normalizedEventOptions, setSelectedSeason]);

  useEffect(() => {
    if (
      event !== "all" &&
      normalizedEventOptions.length > 0 &&
      !normalizedEventOptions.some((option) => String(option.value) === String(event))
    ) {
      setEvent("all");
      setMunicipality("全体");
    }
  }, [event, normalizedEventOptions]);

  useEffect(() => {
    if (event === "all" || activePrefectureAreaIds.length === 0) {
      return;
    }

    const selectedMunicipalityOption = normalizedMunicipalityOptions.find(
      (option) => String(option.value) === String(municipality)
    );
    const municipalityPrefectureAreaId = selectedMunicipalityOption?.prefectureAreaId;

    if (
      municipality !== "全体" &&
      municipalityPrefectureAreaId &&
      !activePrefectureAreaIds.includes(municipalityPrefectureAreaId)
    ) {
      setMunicipality("全体");
    }
  }, [activePrefectureAreaIds, event, municipality, normalizedMunicipalityOptions]);

  useEffect(() => {
    if (
      municipality !== "全体" &&
      !normalizedMunicipalityOptions.some((option) => String(option.value) === String(municipality))
    ) {
      setMunicipality("全体");
    }
  }, [municipality, normalizedMunicipalityOptions]);

  useEffect(() => {
    if (municipality === "全体") {
      return;
    }

    const selectedMunicipalityOption = normalizedMunicipalityOptions.find(
      (option) => String(option.value) === String(municipality)
    );
    const municipalityPrefectureAreaId = selectedMunicipalityOption?.prefectureAreaId;

    if (!municipalityPrefectureAreaId) {
      return;
    }

    const matchedEvent = normalizedEventOptions.find((option) =>
      (option.prefectureAreaIds || []).includes(municipalityPrefectureAreaId)
    );

    if (matchedEvent && String(matchedEvent.value) !== String(event)) {
      setEvent(String(matchedEvent.value));
    }
  }, [event, municipality, normalizedEventOptions, normalizedMunicipalityOptions]);

  useEffect(() => {
    if (
      !fetchFilterEventOptions ||
      !Array.isArray(dateRangePicTree) ||
      !dateRangePicTree[0] ||
      !dateRangePicTree[1]
    ) {
      return;
    }

    getPictreePlotFilterEventOptions();
  }, [dateRangePicTree, fetchFilterEventOptions, getPictreePlotFilterEventOptions]);

  useEffect(() => {
    if (
      !fetchFilterMunicipalityOptions ||
      !Array.isArray(dateRangePicTree) ||
      !dateRangePicTree[0] ||
      !dateRangePicTree[1]
    ) {
      return;
    }

    getPictreePlotFilterMunicipalityOptions({
      plotGameId: event === "all" ? null : event,
    });
  }, [
    dateRangePicTree,
    event,
    fetchFilterMunicipalityOptions,
    getPictreePlotFilterMunicipalityOptions,
  ]);

  const setRange = (newValue) => {
    setDateRangeValue(newValue || [null, null]);

    if (
      !newValue ||
      newValue.length < 2 ||
      !newValue[0] ||
      !newValue[1]
    ) {
      return;
    }

    let startMoment = moment(newValue[0]);
    let endMoment = moment(newValue[1]);

    if (maxRange && startMoment.isValid() && endMoment.isValid()) {
      // 日付だけに正規化
      const startDay = startMoment.startOf("day");
      let   endDay   = endMoment.startOf("day");

      const diffDays = endDay.diff(startDay, "days");

      if (diffDays >= maxRange) {
        // ★ Start を基準に End を maxRange-1 日後に調整
        endDay = startDay.clone().add(maxRange - 1, "days");

        // 必要ならシーズンの最終日にクランプ（maxDate の範囲内に収める）
        if (maxDate) {
          const maxDay = moment(maxDate, "YYYY/MM/DD").startOf("day");
          if (endDay.isAfter(maxDay)) {
            endDay = maxDay;
          }
        }
      }

      startMoment = startDay;
      endMoment   = endDay;
    }

    const startDate = startMoment.format("YYYY-MM-DD");
    const endDate   = endMoment.format("YYYY-MM-DD");

    setDateRangePicTree([startDate, endDate]);
    setDateRangeValue([startDate, endDate]);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <div className={classes.sectionCalendar}>
          <div className={classes.picTreePredefined}>
            <div className={classes.filterGroup}>
              <Typography className={classes.filterLabel}>イベント：</Typography>
              <TextField
                select
                variant="outlined"
                size="small"
                className={classes.field}
                value={event}
                onChange={(e) => {
                  setEvent(e.target.value);
                }}
              >
                <MenuItem value="all">全体</MenuItem>
                {normalizedEventOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <Typography className={classes.filterLabel}>自治体：</Typography>
              <TextField
                select
                variant="outlined"
                size="small"
                className={classes.field}
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
              >
                {displayMunicipalityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
          <DateRangePicker
            startText="Start"
            endText="End"
            disableHighlightToday={true}
            showToolbar={false}
            allowSameDateSelection={true}
            minDate={minDate}
            maxDate={maxDate}
            value={dateRangeValue}
            onChange={(newValue) => setRange(newValue)}
            onError={([startReason, endReason], [start, end]) => {
              if (startReason === "invalidRange") {
                setError([null, null]);
                return;
              }
              setError([startReason, endReason]);
            }}
            shouldDisableDate={(day) => {
              // maxRange 未指定なら制限なし
              if (!maxRange) return false;

              const [start, end] = dateRangeValue || [];
              const dayM   = moment(day).startOf("day");
              const startM = start ? moment(start).startOf("day") : null;
              const endM   = end   ? moment(end).startOf("day")   : null;

              // --- パターン1: Start 済み・End 未選択 → End を選択中 ---
              if (startM && !endM) {
                const diff = dayM.diff(startM, "days");
                // End は Start 以降かつ maxRange 日以内だけ許可
                if (diff < 0) return true;          // Start より前は NG
                if (diff >= maxRange) return true;   // maxRange 超えたら NG
                return false;
              }

              // --- パターン2: End 済み・Start 未選択 → Start を選択中 ---
              if (!startM && endM) {
                const diff = endM.diff(dayM, "days");

                // Start は End 以前かつ maxRange 日以内だけ許可
                if (diff < 0) return true;          // End より後ろは NG
                if (diff > maxRange) return true;   // maxRange 超えたら NG

                return false;
              }

              // --- パターン3: まだどちらも選んでない / 両方選び直し中 ---
              // この場合は制限なし（minDate/maxDate だけ効く）
              return false;
            }}
            renderInput={(startProps, endProps) => (
              <div className={classes.container}>
                <TextField
                  id="standard-basic"
                  {...startProps}
                  error={Boolean(error[0])}
                  size="small"
                  disabled={page === "pictree-map" ? true : false}
                  helperText=""
                  className={classes.dateField}
                />
                <DateRangeDelimiter>
                  <span className={classes.delimiter}>~</span>
                </DateRangeDelimiter>
                <TextField
                  id="standard-basic"
                  {...endProps}
                  error={Boolean(error[0])}
                  size="small"
                  disabled={page === "pictree-map" ? true : false}
                  helperText=""
                  className={classes.dateField}
                />
              </div>
            )}
          />
          <Button
            className={classes.dataBtn}
            variant="contained"
            color="primary"
            onClick={() => {
              if (typeof onGoClick === "function") {
                onGoClick({
                  event: event === "all" ? null : event,
                  municipality: municipality === "全体" ? null : municipality,
                  dateRange: dateRangeValue,
                });
              }
            }}
          >
            GO
          </Button>
        </div>
      </div>
    </div>
  );
};

CalendarRangePickerPlot.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  btnStateDataFetch: state.page.btnStateDataFetch,
  dateRangePicTree: state.page.dateRangePicTree,
  selectedSeason: state.pictree.selectedSeason,
  plotMunicipality: state.page.plotMunicipality,
  pictreeView: state.pictree.pictreeView,
  pictreeChartSize: state.pictree.chartSize,
  pictreeMapType: state.pictree.mapType,
  plotEventOptions: state.pictreePlot.filterEventOptions,
  plotMunicipalityOptions: state.pictreePlot.filterMunicipalityOptions,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTree: (param) => dispatch(setDateRangePicTree(param)),
  setPlotMunicipality: (param) => dispatch(setPlotMunicipality(param)),
  setSelectedSeason: (param) => dispatch(setSelectedSeason(param)),
  getPictreePlotFilterEventOptions: (param) => dispatch(getPictreePlotFilterEventOptions(param)),
  getPictreePlotFilterMunicipalityOptions: (param) => dispatch(getPictreePlotFilterMunicipalityOptions(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(CalendarRangePickerPlot));
